/**
 * Terrain Grid Core — grid data, coordinate conversion, column cache, core queries.
 *
 * Cell values (see CELL constant in constants.ts):
 *   0 = EMPTY  — air, no collision
 *   1 = SAND   — loose terrain, destroyed by any explosion
 *   2 = WIREFRAME — structural terrain, requires heavy weapons
 *
 * All collision/physics queries treat any non-zero cell as solid (> 0).
 * Use getCellMaterial() when material distinction matters (rendering, destruction).
 *
 * Grid coords: world coords / CELL_SIZE
 * World coords: 0..2560, 0..1440
 * Grid coords: 0..1280, 0..720
 */

import { TERRAIN, WORLD, CELL } from '../constants';
import type { CellType } from '../types';

const { CELL_SIZE } = TERRAIN;

// ─── Module-level state ─────────────────────────────────────────────────────────

let grid: Uint8Array | null = null;

// Dual cache: floor (bottom-up) + ceiling (top-down)
let columnFloorCache: Float32Array | null = null;    // world-Y of topmost floor cell
let columnCeilingCache: Float32Array | null = null;  // world-Y of bottommost ceiling cell (NaN = no ceiling)

// Scorch intensity per cell (0-255) — terrain damage memory
let scorchMap: Uint8Array | null = null;
let scorchDecayTimer = 0;

// ─── State accessors (for sibling modules) ──────────────────────────────────────

export function getGrid(): Uint8Array | null {
  return grid;
}

export function getScorchMap(): Uint8Array | null {
  return scorchMap;
}

export function getScorchDecayTimer(): number {
  return scorchDecayTimer;
}

export function setScorchDecayTimer(value: number): void {
  scorchDecayTimer = value;
}

// ─── Coordinate conversion ──────────────────────────────────────────────────────

export function worldToGridX(wx: number): number {
  return Math.floor(wx / CELL_SIZE);
}

export function worldToGridY(wy: number): number {
  return Math.floor(wy / CELL_SIZE);
}

export function gridToWorldX(gx: number): number {
  return gx * CELL_SIZE + CELL_SIZE * 0.5;
}

export function gridToWorldY(gy: number): number {
  return gy * CELL_SIZE + CELL_SIZE * 0.5;
}

function inBounds(gx: number, gy: number): boolean {
  return gx >= 0 && gx < TERRAIN.GRID_WIDTH && gy >= 0 && gy < TERRAIN.GRID_HEIGHT;
}

// ─── Grid lifecycle ─────────────────────────────────────────────────────────────

export function createGrid(): Uint8Array {
  grid = new Uint8Array(TERRAIN.GRID_WIDTH * TERRAIN.GRID_HEIGHT);
  columnFloorCache = new Float32Array(TERRAIN.GRID_WIDTH);
  columnCeilingCache = new Float32Array(TERRAIN.GRID_WIDTH);
  columnCeilingCache.fill(NaN);
  scorchMap = new Uint8Array(TERRAIN.GRID_WIDTH * TERRAIN.GRID_HEIGHT);
  scorchDecayTimer = 0;
  return grid;
}

// ─── Core queries (world coordinates) ───────────────────────────────────────────

export function isPointInTerrain(wx: number, wy: number, sandOnly = false): boolean {
  if (!grid) return false;
  const gx = worldToGridX(wx);
  const gy = worldToGridY(wy);
  if (!inBounds(gx, gy)) return false;
  const cell = grid[gy * TERRAIN.GRID_WIDTH + gx];
  return sandOnly ? cell === CELL.SAND : cell > 0;
}

function getCellMaterial(wx: number, wy: number): CellType {
  if (!grid) return CELL.EMPTY;
  const gx = worldToGridX(wx);
  const gy = worldToGridY(wy);
  if (!inBounds(gx, gy)) return CELL.EMPTY;
  return grid[gy * TERRAIN.GRID_WIDTH + gx] as CellType;
}

/** Get the raw cell value at a world coordinate. Alias for getCellMaterial — exported for platform logic. */
export const getCellAt = getCellMaterial;

export function getGroundY(wx: number): number {
  if (!grid || !columnFloorCache) return WORLD.VIRTUAL_HEIGHT;
  const gx = worldToGridX(wx);
  if (gx < 0 || gx >= TERRAIN.GRID_WIDTH) return WORLD.VIRTUAL_HEIGHT;
  return columnFloorCache[gx];
}

/**
 * Find the ground surface at or below a given Y position.
 * Unlike getGroundY() which returns the column's overall floor surface,
 * this skips terrain ABOVE the query point — correct for "is there ground
 * beneath me?" checks when floating platforms exist above the tank.
 */
export function getGroundYBelow(wx: number, fromY: number): number {
  if (!grid) return WORLD.VIRTUAL_HEIGHT;
  const gx = worldToGridX(wx);
  if (gx < 0 || gx >= TERRAIN.GRID_WIDTH) return WORLD.VIRTUAL_HEIGHT;
  const startGy = Math.max(0, worldToGridY(fromY));
  for (let gy = startGy; gy < TERRAIN.GRID_HEIGHT; gy++) {
    if (grid[gy * TERRAIN.GRID_WIDTH + gx] > 0) {
      return gy * CELL_SIZE;
    }
  }
  return WORLD.VIRTUAL_HEIGHT;
}

/** World-Y of the bottom of the ceiling at this X. Returns NaN if no ceiling. */
function getCeilingBottomY(wx: number): number {
  if (!grid || !columnCeilingCache) return NaN;
  const gx = worldToGridX(wx);
  if (gx < 0 || gx >= TERRAIN.GRID_WIDTH) return NaN;
  return columnCeilingCache[gx];
}

/** Whether a ceiling exists at this world X. */
function hasCeiling(wx: number): boolean {
  return !isNaN(getCeilingBottomY(wx));
}

/** Gap height between floor top and ceiling bottom at this X. Returns Infinity if no ceiling. */
function getGapHeight(wx: number): number {
  const floorY = getGroundY(wx);
  const ceilY = getCeilingBottomY(wx);
  if (isNaN(ceilY)) return Infinity;
  return floorY - ceilY; // positive = gap exists (ceiling is above floor)
}

export function getSurfaceNormal(wx: number, wy: number): { nx: number; ny: number } {
  if (!grid) return { nx: 0, ny: -1 };

  const sampleRadius = 3;
  const gx = worldToGridX(wx);
  const gy = worldToGridY(wy);

  let sumNx = 0;
  let sumNy = 0;

  for (let dy = -sampleRadius; dy <= sampleRadius; dy++) {
    for (let dx = -sampleRadius; dx <= sampleRadius; dx++) {
      const sx = gx + dx;
      const sy = gy + dy;
      if (inBounds(sx, sy) && grid[sy * TERRAIN.GRID_WIDTH + sx] > 0) {
        const pushDx = gx - sx;
        const pushDy = gy - sy;
        const dist = Math.sqrt(pushDx * pushDx + pushDy * pushDy);
        if (dist > 0) {
          const weight = 1 / (dist + 0.5);
          sumNx += (pushDx / dist) * weight;
          sumNy += (pushDy / dist) * weight;
        }
      }
    }
  }

  const len = Math.sqrt(sumNx * sumNx + sumNy * sumNy);
  if (len < 0.001) return { nx: 0, ny: -1 };
  return { nx: sumNx / len, ny: sumNy / len };
}

// ─── Dual column cache (floor + ceiling) ────────────────────────────────────────

/**
 * Find the topmost cell of the floor band (bottom-up scan).
 * This correctly skips ceiling cells by scanning from the bottom.
 * Returns world-Y of the top of the floor band, or VIRTUAL_HEIGHT if no floor.
 */
function findFloorTop(gx: number): number {
  if (!grid) return WORLD.VIRTUAL_HEIGHT;

  // Scan bottom-up: find first solid, then continue up to find where band ends
  let foundSolid = false;
  for (let gy = TERRAIN.GRID_HEIGHT - 1; gy >= 0; gy--) {
    if (grid[gy * TERRAIN.GRID_WIDTH + gx] > 0) {
      foundSolid = true;
    } else if (foundSolid) {
      // Just left the floor band — return the cell below (first solid)
      return (gy + 1) * CELL_SIZE;
    }
  }
  // Entire column is solid from top
  if (foundSolid) return 0;
  return WORLD.VIRTUAL_HEIGHT;
}

/**
 * Find the bottom of the ceiling band (top-down scan).
 * Returns world-Y of the bottom edge of the ceiling, or NaN if no ceiling.
 */
function findCeilingBottom(gx: number): number {
  if (!grid) return NaN;

  let foundSolid = false;
  for (let gy = 0; gy < TERRAIN.GRID_HEIGHT; gy++) {
    if (grid[gy * TERRAIN.GRID_WIDTH + gx] > 0) {
      foundSolid = true;
    } else if (foundSolid) {
      // Just left the ceiling band — return this Y (first air below ceiling)
      return gy * CELL_SIZE;
    }
  }
  // If entire column is solid or no ceiling found
  // Check: if we found solid but never air, the whole column is solid — no separate ceiling
  return NaN;
}

export function rebuildColumnTopCache(): void {
  if (!grid) return;
  if (!columnFloorCache) columnFloorCache = new Float32Array(TERRAIN.GRID_WIDTH);
  if (!columnCeilingCache) {
    columnCeilingCache = new Float32Array(TERRAIN.GRID_WIDTH);
    columnCeilingCache.fill(NaN);
  }

  for (let gx = 0; gx < TERRAIN.GRID_WIDTH; gx++) {
    columnFloorCache[gx] = findFloorTop(gx);
    columnCeilingCache[gx] = findCeilingBottom(gx);
  }
}

export function rebuildColumnTopCacheRange(minGx: number, maxGx: number): void {
  if (!grid || !columnFloorCache || !columnCeilingCache) return;
  const startGx = Math.max(0, minGx);
  const endGx = Math.min(TERRAIN.GRID_WIDTH - 1, maxGx);

  for (let gx = startGx; gx <= endGx; gx++) {
    columnFloorCache[gx] = findFloorTop(gx);
    columnCeilingCache[gx] = findCeilingBottom(gx);
  }
}
